import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackMovedBoards } from '@air/analytics';
import { Boards } from '@air/api';
import {
  type BetweenBoardClipsInput,
  type BetweenBoardsInput,
  type BetweenPersonalBoardsInput,
  type Board,
  type Clip,
  type ListResponse,
  type PersonalBoardIdentifier,
} from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type GalleryItemType } from '~/components/Gallery/types';
import { type GalleryItemToRearrange } from '~/components/Shared/ClipList/RearrangeableRow';
import { type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { getPrivateBoardsListKey, PRIVATE_BOARDS_LIST_KEY } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useFavoriteBoardsParams } from '~/components/v2/hooks/useFavoriteBoardsParams';
import { getFavoritedMessage } from '~/constants/messages';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { moveBoardRequestAction } from '~/store/centralizedBoard/actions';
import { selectedClipIdsSelector, selectedPrivateBoardsSelector } from '~/store/selectedItems/selectors';
import { useFavoriteBoards } from '~/swr-hooks/boards/useFavoriteBoards';
import { getFavoriteBoardsKey } from '~/swr-hooks/boards/useFavoriteBoardsList';
import { BOARD_SUBBOARDS } from '~/swr-hooks/boards/useSubBoardsList';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import { insertItemsInOrder, moveItemToIndex } from '~/utils/ArrayUtils';
import { calculateRearrangeData, getHoverIndex, getRearrangeDragItem } from '~/utils/DragUtils';
import { useRearrangeBoardsInAllLists } from '~/utils/mutateUtils/AllBoards';
import { useRearrangeAssetsInGalleryView } from '~/utils/mutateUtils/GalleryAssets';
import { useRemoveBoardsFromAllViews } from '~/utils/mutateUtils/GalleryBoards';
import { getPersonalBoardBetweenIds, mergeReorderedSiblingsIntoFlatBoards } from '~/utils/mutateUtils/mutators';
import {
  useAddBoardsToSideNav,
  useChangeSideBoardsParent,
  useRearrangeFavoriteBoardsInSideNav,
  useRemoveBoardsFromSideNav,
} from '~/utils/mutateUtils/SideNavBoards';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export const useRearrangeBoards = () => {
  const dispatch = useDispatch();
  const { showToast } = useToasts();
  const { removeBoardsFromAllViews } = useRemoveBoardsFromAllViews();
  const { rearrangeBoardsInAllLists } = useRearrangeBoardsInAllLists();
  const { changeSideBoardsParent } = useChangeSideBoardsParent();
  const { addBoardsToSideNav } = useAddBoardsToSideNav();
  const { removeBoardsFromSideNav } = useRemoveBoardsFromSideNav();
  const { rearrangeFavoriteBoardsInSideNav } = useRearrangeFavoriteBoardsInSideNav();
  const store = useAirStore();
  const { data: account } = useAccountContext();
  const { favoriteBoards } = useFavoriteBoards();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackMovedBoards } = useTrackMovedBoards();
  const queryClient = useQueryClient();
  const { getFavoriteBoardsParams } = useFavoriteBoardsParams();

  const workspaceId = currentWorkspace?.id;

  const getFlatFavoriteBoardsFromCache = useCallback((): Board[] => {
    if (!workspaceId) {
      return [];
    }

    const readFlat = (queryKey: readonly unknown[]) => {
      const entries = queryClient.getQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey });
      const data = entries.find(([, value]) => value)?.[1];

      return data?.pages.flatMap((page) => page.data) ?? [];
    };

    const fromV2 = readFlat(getPrivateBoardsListKey(getFavoriteBoardsParams()));

    if (fromV2.length) {
      return fromV2;
    }

    return readFlat(getFavoriteBoardsKey(workspaceId));
  }, [getFavoriteBoardsParams, queryClient, workspaceId]);

  const rearrangeBoards = useCallback(
    async ({
      parentBoardId = null,
      library,
      boards,
      draggedItem,
      adjacentItem,
    }: {
      boards: Board[];
      draggedItem: GetBoardDragItem;
      adjacentItem: RearrangableItem;
      parentBoardId: string | null;
      library?: Board['library'];
    }) => {
      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      let boardsToMove: string[];
      const draggedBoard = draggedItem.firstItem as Board;
      const rearrangeDragItem = getRearrangeDragItem(boards, draggedItem);
      const { nextItemId, previousItemId } = calculateRearrangeData(boards, rearrangeDragItem, adjacentItem);

      const selectedBoards = selectedPrivateBoardsSelector(store.getState());
      const selectedBoardsIds = selectedBoards.map(({ id }) => id);
      const oldLibraryId = draggedBoard?.library?.id;
      // When `library` isn't passed in (e.g. gallery board-to-board reorder inside the
      // same parent), fall back to the dragged board's library so we don't misread
      // the move as a cross-library move and skip `Boards.updateBoardPositions`.
      // For real cross-parent moves the caller intentionally omits `library`, so we
      // preserve that undefined behavior in that branch.
      const destinationLibrary =
        library ?? (draggedItem.parentId === parentBoardId ? draggedBoard?.library : undefined);
      const boardsById = [draggedBoard, ...selectedBoards, ...boards].reduce(
        (acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        },
        {} as { [id: string]: Board },
      );

      if (selectedBoardsIds.length) {
        boardsToMove = [...selectedBoardsIds];
      } else {
        boardsToMove = [draggedItem.firstItem.id];
      }

      const adjacentBoards: BetweenBoardsInput = {};

      if (previousItemId) {
        adjacentBoards.moveBeforeId = previousItemId;
      }

      if (nextItemId) {
        adjacentBoards.moveAfterId = nextItemId;
      }

      // Build the optimistic post-reorder array. `insertItemsInOrder` positions
      // the moved block at the raw drop-gap index, handling same-parent and
      // cross-parent moves uniformly.
      const updatedBoards = [...boards];
      const hoverIndex = getHoverIndex(adjacentItem);

      insertItemsInOrder({
        array: updatedBoards,
        itemIds: boardsToMove,
        itemsById: boardsById,
        insertBeforeIndex: hoverIndex,
      });

      const boardsToMoveSet = new Set(boardsToMove);
      const movedBoardsInfo = updatedBoards.filter((b) => boardsToMoveSet.has(b.id));
      const cacheInsertAtIndex = updatedBoards.findIndex((b) => boardsToMoveSet.has(b.id));

      if (draggedItem.parentId !== parentBoardId || destinationLibrary?.id !== oldLibraryId) {
        dispatch(
          moveBoardRequestAction({
            boards: [...boards, ...selectedBoards].map((board) => ({
              ...board,
              ancestors: parentBoardId ? board.ancestors : [],
              library: destinationLibrary,
              parentId: parentBoardId,
            })),
            newParentId: parentBoardId,
            oldParentId: draggedItem.parentId,
          }),
        );

        Boards.batchMoveBoards({
          boardIds: boardsToMove,
          libraryId: destinationLibrary?.id,
          newParentId: parentBoardId,
          workspaceId,
        }).then(() => {
          trackMovedBoards({
            board_ids: boardsToMove,
            new_library_id: destinationLibrary?.id,
            new_parent_board_id: parentBoardId,
          });
        });
      }

      try {
        // Check if this is a cross-library move to a non-Custom Sort library
        const isCrossLibraryMove = destinationLibrary?.id !== oldLibraryId;
        const destinationLibrarySort = destinationLibrary?.id ? getSubNavSortValue(destinationLibrary.id) : undefined;
        const isDestinationCustomSort = destinationLibrarySort?.libraryBoardSorts?.[0]?.name === 'customSort';

        // Only update positions if:
        // 1. It's not a cross-library move, OR
        // 2. It's a cross-library move to a Custom Sort library
        const shouldUpdatePosition = !isCrossLibraryMove || isDestinationCustomSort;

        if ((adjacentBoards.moveAfterId || adjacentBoards.moveBeforeId) && shouldUpdatePosition) {
          // when all boards are library boards, no boards are shown in workspace boards sidenav
          Boards.updateBoardPositions({
            betweenIds: adjacentBoards,
            idsToMove: boardsToMove,
            parentBoardId: parentBoardId ?? '',
            workspaceId,
          });
        }

        if (movedBoardsInfo) {
          const rearrangedWithParent = movedBoardsInfo.map((board) => ({
            ...board,
            library: oldLibraryId === destinationLibrary?.id ? board.library : destinationLibrary,
            parentId: parentBoardId === 'root' ? null : parentBoardId,
          }));

          const boardIdsToRearrange = movedBoardsInfo.map((board) => board.id);

          if (draggedItem.parentId !== parentBoardId) {
            if (draggedItem.parentId) {
              removeBoardsFromAllViews({ boardsIdsToRemove: boardIdsToRearrange, parentBoardId: draggedItem.parentId });
            }

            // Awaited so the cancelQueries inside changeSideBoardsParent completes
            // before rearrangeBoardsInAllLists writes to the same sidenav keys —
            // otherwise the deferred parent-change write lands after the rearrange
            // and produces duplicate boards.
            await changeSideBoardsParent({
              newBoards: rearrangedWithParent,
              oldLibraryId,
              oldParentId: draggedItem.parentId,
              workspaceId: movedBoardsInfo[0].workspaceId,
            });
          } else if (oldLibraryId !== destinationLibrary?.id) {
            removeBoardsFromAllViews({ boardsIdsToRemove: boardIdsToRearrange, libraryId: oldLibraryId });

            // Get library-specific sorts for both old and new libraries
            const oldLibrarySort = oldLibraryId ? getSubNavSortValue(oldLibraryId) : undefined;
            const oldLibrarySortField = oldLibrarySort?.libraryBoardSorts?.[0];

            const newLibrarySort = destinationLibrary?.id ? getSubNavSortValue(destinationLibrary.id) : undefined;
            const newLibrarySortField = newLibrarySort?.libraryBoardSorts?.[0];

            removeBoardsFromSideNav({
              boardIds: rearrangedWithParent.map((b) => b.id),
              libraryId: oldLibraryId,
              navSortParams: oldLibrarySortField,
              workspaceId: movedBoardsInfo[0].workspaceId,
            });
            // Awaited for the same reason as changeSideBoardsParent above.
            await addBoardsToSideNav(rearrangedWithParent, newLibrarySortField);
          }
          rearrangeBoardsInAllLists(rearrangedWithParent, cacheInsertAtIndex);
        }
      } catch (error) {
        reportErrorToBugsnag({
          context: `Could not update board positions`,
          error,
          metadata: {
            data: {
              adjacentBoards,
              boardsToMove,
              parentBoardId,
            },
          },
        });
      }
    },
    [
      workspaceId,
      store,
      dispatch,
      trackMovedBoards,
      rearrangeBoardsInAllLists,
      changeSideBoardsParent,
      removeBoardsFromAllViews,
      getSubNavSortValue,
      removeBoardsFromSideNav,
      addBoardsToSideNav,
    ],
  );

  const rearrangeFavoriteBoards = useCallback(
    async ({
      parentBoardId = null,
      boards,
      draggedItem,
      adjacentItem,
    }: {
      boards: Board[];
      draggedItem: GetBoardDragItem;
      adjacentItem: RearrangableItem;
      parentBoardId: string | null;
    }) => {
      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      if (!account?.id) {
        throw new Error(`No user account!`);
      }

      const updatedBoards = [...boards];
      let boardsToMove: string[];
      const draggedBoard = draggedItem.firstItem as Board;
      const rearrangeDragItem = getRearrangeDragItem(boards, draggedItem);
      const { newIndex, nextItemId, previousItemId } = calculateRearrangeData(boards, rearrangeDragItem, adjacentItem);
      const hoverIndex = getHoverIndex(adjacentItem);

      const selectedBoards = selectedPrivateBoardsSelector(store.getState());
      const selectedBoardsIds = selectedBoards.map(({ id }) => id);
      const boardsById = [draggedBoard, ...selectedBoards, ...boards].reduce(
        (acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        },
        {} as { [id: string]: Board },
      );
      const draggedBoardIsFavorited = draggedBoard.hasCurrentUser;

      if (selectedBoardsIds.length) {
        insertItemsInOrder({
          array: updatedBoards,
          itemIds: selectedBoardsIds,
          itemsById: boardsById,
          insertBeforeIndex: hoverIndex,
        });

        boardsToMove = [...selectedBoardsIds];
      } else {
        // we dragged already favorited board - change its index
        if (draggedBoardIsFavorited) {
          moveItemToIndex(updatedBoards, rearrangeDragItem.index, newIndex);
        } else {
          insertItemsInOrder({
            array: updatedBoards,
            itemIds: [draggedItem.firstItem.id],
            itemsById: boardsById,
            insertBeforeIndex: hoverIndex,
          });
        }

        boardsToMove = [draggedItem.firstItem.id];
      }

      const normalizedParentBoardId = parentBoardId === 'root' || !parentBoardId ? null : parentBoardId;
      const movedAreInPersonalList = boardsToMove.every((id) => boardsById[id]?.hasCurrentUser);

      if (normalizedParentBoardId && !movedAreInPersonalList) {
        return rearrangeBoards({
          parentBoardId: normalizedParentBoardId,
          boards,
          draggedItem,
          adjacentItem,
          library: draggedBoard.library,
        });
      }

      const boardsToMoveSet = new Set(boardsToMove);
      const movedBoardsInfo = updatedBoards.filter((b) => boardsToMoveSet.has(b.id));
      const cacheInsertAtIndex = updatedBoards.findIndex((b) => boardsToMoveSet.has(b.id));

      const betweenIds: BetweenPersonalBoardsInput = {};

      if (normalizedParentBoardId && movedAreInPersonalList) {
        const reorderedSiblings = updatedBoards.map((board) => ({
          ...board,
          hasCurrentUser: true,
          parentId: normalizedParentBoardId,
        }));
        const mergedFlat = mergeReorderedSiblingsIntoFlatBoards(getFlatFavoriteBoardsFromCache(), reorderedSiblings);
        const flatInsertIndex = mergedFlat.findIndex((b) => boardsToMoveSet.has(b.id));
        const { moveAfterId, moveBeforeId } = getPersonalBoardBetweenIds(
          mergedFlat,
          flatInsertIndex,
          boardsToMove.length,
        );

        if (moveBeforeId) {
          betweenIds.moveBeforeId = {
            accountId: account.id,
            boardId: moveBeforeId,
          };
        }

        if (moveAfterId) {
          betweenIds.moveAfterId = {
            accountId: account.id,
            boardId: moveAfterId,
          };
        }
      } else {
        if (previousItemId) {
          betweenIds.moveBeforeId = {
            accountId: account?.id,
            boardId: previousItemId,
          };
        }

        if (nextItemId) {
          betweenIds.moveAfterId = {
            accountId: account?.id,
            boardId: nextItemId,
          };
        }
      }

      const idsToMove: PersonalBoardIdentifier[] = boardsToMove.map((boardId) => ({
        accountId: account?.id,
        boardId,
      }));

      if (!normalizedParentBoardId && !draggedBoardIsFavorited) {
        favoriteBoards({
          boards: boardsToMove
            .filter((boardId) => boardsById[boardId] && !boardsById[boardId].hasCurrentUser)
            .map((boardId) => ({ ...boardsById[boardId], title: draggedBoard.title })),
          trackLocation: 'drag-n-drop',
        });

        showToast(getFavoritedMessage(true, boardsToMove.length));
      }

      try {
        const rearrangedWithParent = movedBoardsInfo.map((board) => ({
          ...board,
          hasCurrentUser: true,
          parentId: normalizedParentBoardId,
        }));

        /**
         * Subboards can be favorited independently from its ancestors
         * repositioning them around the main level of the FAVORITES BOARD section should not be treated like they are being dragged out as a root level
         */
        const isFavoritedSubboardReposition = draggedBoard.hasCurrentUser && parentBoardId === null;

        if (!isFavoritedSubboardReposition && draggedItem.parentId !== parentBoardId) {
          changeSideBoardsParent({
            newBoards: rearrangedWithParent,
            oldLibraryId: draggedBoard.library?.id,
            oldParentId: draggedItem.parentId,
            workspaceId: movedBoardsInfo[0].workspaceId,
          });
        }

        if (normalizedParentBoardId) {
          rearrangeFavoriteBoardsInSideNav(rearrangedWithParent, cacheInsertAtIndex, {
            parentBoardId: normalizedParentBoardId,
            libraryId: draggedBoard.library?.id ?? updatedBoards[0]?.library?.id,
            reorderedSiblings: updatedBoards.map((board) => ({
              ...board,
              hasCurrentUser: true,
              parentId: normalizedParentBoardId,
            })),
          });
        } else {
          rearrangeFavoriteBoardsInSideNav(rearrangedWithParent, cacheInsertAtIndex);
        }

        await Boards.updatePersonalBoardPositions({
          betweenIds,
          idsToMove,
          workspaceId,
        });
      } catch (error) {
        reportErrorToBugsnag({
          context: `Could not update personal positions`,
          error,
          metadata: {
            data: {
              betweenIds,
              boardsToMove,
              idsToMove,
            },
          },
        });

        // API failed after the optimistic updates — refetch the affected lists to resync
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: getFavoriteBoardsKey(workspaceId) }),
          queryClient.invalidateQueries({ queryKey: getPrivateBoardsListKey(getFavoriteBoardsParams()) }),
          ...(normalizedParentBoardId
            ? [
                queryClient.invalidateQueries({ queryKey: [BOARD_SUBBOARDS, { boardId: normalizedParentBoardId }] }),
                queryClient.invalidateQueries({
                  queryKey: [PRIVATE_BOARDS_LIST_KEY, { view: normalizedParentBoardId }],
                }),
              ]
            : []),
        ]);
      }
    },
    [
      account?.id,
      changeSideBoardsParent,
      favoriteBoards,
      getFavoriteBoardsParams,
      getFlatFavoriteBoardsFromCache,
      queryClient,
      rearrangeBoards,
      rearrangeFavoriteBoardsInSideNav,
      showToast,
      store,
      workspaceId,
    ],
  );

  return {
    rearrangeBoards,
    rearrangeFavoriteBoards,
  };
};

export const useRearrangeAssets = () => {
  const store = useAirStore();
  const { rearrangeAssetsInGalleryView } = useRearrangeAssetsInGalleryView();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const rearrangeAssets = useCallback(
    async (
      assets: Clip[],
      draggedItem: GalleryItemToRearrange,
      adjacentItem: RearrangableItem,
      type: GalleryItemType.asset | GalleryItemType.file,
    ) => {
      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      const updatedAssets = [...assets];
      let boardClipsToMove: Array<{
        reelId: string;
        clipId: Clip['id'];
      }> = [];

      const { newIndex, nextItemId, previousItemId } = calculateRearrangeData(assets, draggedItem, adjacentItem);

      const adjacentClips: BetweenBoardClipsInput = {};

      const currentBoardId = getBoardIdFromPath(window.location.pathname);

      if (!currentBoardId) {
        return;
      }

      // TODO: move it to hook, but be careful with this selector - we don't want to update everything when selected items change
      const selectedClips = selectedClipIdsSelector(store.getState());

      if (previousItemId) {
        adjacentClips.moveBeforeBoardClip = {
          clipId: previousItemId,
          reelId: currentBoardId,
        };
      }

      if (nextItemId) {
        adjacentClips.moveAfterBoardClip = {
          clipId: nextItemId,
          reelId: currentBoardId,
        };
      }

      if (selectedClips.length) {
        // Sort by current gallery index — backend and optimistic view must agree on order, so visual order wins over Redux click order.
        // Insert before the raw hover gap: insertItemsInOrder strips the moved ids before counting, so passing the source-adjusted `newIndex` would land the block one position too late.
        const assetsById = assets.reduce(
          (acc, asset) => {
            acc[asset.id] = asset;
            return acc;
          },
          {} as { [id: string]: Clip },
        );
        const orderedSelectedClips = [...selectedClips].sort(
          (a, b) => assets.findIndex((asset) => asset.id === a) - assets.findIndex((asset) => asset.id === b),
        );

        insertItemsInOrder({
          array: updatedAssets,
          itemIds: orderedSelectedClips,
          itemsById: assetsById,
          insertBeforeIndex: getHoverIndex(adjacentItem),
        });

        boardClipsToMove = orderedSelectedClips.map((id) => ({ clipId: id, reelId: currentBoardId }));
      } else {
        const assetToMove = assets[draggedItem.index];

        moveItemToIndex(updatedAssets, draggedItem.index, newIndex);
        boardClipsToMove = [{ clipId: assetToMove.id, reelId: currentBoardId }];
      }

      try {
        Boards.updateBoardClipPositions({
          betweenBoardClips: adjacentClips,
          boardClipsToMove,
          parentBoardId: currentBoardId,
          workspaceId,
        });

        if (currentBoardId) {
          await rearrangeAssetsInGalleryView(currentBoardId, updatedAssets, type);
        }
      } catch (error) {
        reportErrorToBugsnag({
          context: `Could not update assets positions`,
          error,
          metadata: {
            data: {
              adjacentClips,
              boardClipsToMove,
              currentBoardId,
            },
          },
        });
      }
    },
    [rearrangeAssetsInGalleryView, store, workspaceId],
  );

  return {
    rearrangeAssets,
  };
};
